/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import React, { PropsWithChildren } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useSetRecoilState } from "recoil";
import * as CampaignApi from "../../../../api/Campaign";
import {
  ActiveCampaignAtom,
  TempCampaignAtom,
} from "../../../../atoms/CampaignAtom";
import { CampaingBuilderFormAtom } from "../../../../atoms/CampaingBuilderFormAtom";
import { ILearnisticApp, ITagConditions } from "../../../../generated/Api";
import { Campaign, INode } from "../../../../types/CampaignTypes";
import { ITag } from "../../../../types/TagTypes";
import { useModal } from "../../../Modal/Modal";
import StyledButton from "../../../StyledButton";

const Container = styled.div`
  height: 100%;
  width: 100%;
  background-color: white;
  border-left: 1px solid #dddddd;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: scroll;
`;

const BottomBar = styled.div`
  border-top: 1px solid #dddddd;
  padding: 16px 30px;
  display: flex;
  justify-content: flex-end;
  width: 100%;
  position: relative;
`;

export interface DefaultFormProps {
  nodeIndex: number;
  action: "edit" | "create";
  initial?: boolean;
  stopCampaign?: boolean;
}

export interface FormInstance {
  updateInitialTags?: (tags: (ITag | ILearnisticApp)[]) => void;
  updateStopTags?: (tags: (ITag | ILearnisticApp)[]) => void;
  updateNodeDescription: (description: string) => void;
  updateCampaign: (
    newAction: INode,
    successMessage?: string,
    delayAction?: INode
  ) => void;
  tempCampaign: Campaign | null;
}
interface FormProps {
  children: (props: PropsWithChildren<FormInstance>) => {
    Component: JSX.Element;
    saveForm: () => any;
  };
}

const Form = ({
  children,
  action,
  nodeIndex,
  initial,
  stopCampaign,
}: FormProps & DefaultFormProps) => {
  const setForm = useSetRecoilState(CampaingBuilderFormAtom);
  const [tempCampaign, setTempCampaign] = useRecoilState(TempCampaignAtom);
  const [activeCampaign, setActiveCampaign] = useRecoilState<Campaign | null>(
    ActiveCampaignAtom
  );

  function updateNodeDescription(name: string) {
    setTempCampaign((oldTemp: Campaign | null) => {
      if (oldTemp && oldTemp.nodes[nodeIndex]) {
        const nodeCopy: INode = {
          ...oldTemp.nodes[nodeIndex],
          name,
        };
        const campaingCopy = { ...oldTemp, nodes: [...oldTemp.nodes] };
        campaingCopy.nodes.splice(nodeIndex, 1, nodeCopy);
        return campaingCopy;
      } else return null;
    });
  }

  // That's punk!
  async function updateCampaign(
    newAction: INode,
    successMessage?: string,
    delayAction?: INode
  ) {}

  async function updateInitialTags(selectedTags: (ITag | ILearnisticApp)[]) {
    try {
      const currentCampaign = tempCampaign as Campaign;

      const { _id: campaignId } = currentCampaign;

      let newTagConditions = selectedTags.map((st: ITag | ILearnisticApp) => {
        if ((st as any).tags) {
          return {
            tagId: "LEARNISTIC",
            action: "add",
            learnisticTag: st as ILearnisticApp,
          } as ITagConditions;
        } else {
          return {
            tagId: (st as ITag)._id,
            action: "add",
          } as ITagConditions;
        }
      });

      let updatedCampaign = {
        ...currentCampaign,
        tagConditions: newTagConditions,
      };

      const response = await CampaignApi.updateCampaign(
        campaignId,
        updatedCampaign
      );

      if (response) {
        setTempCampaign(updatedCampaign);
      } else {
        toast("For some reason your campaign doesn't exist anymore", {
          type: "warning",
        });
      }
    } catch (err) {
      toast("There was an error, try again later", { type: "error" });
    }
  }

  async function updateStopTags(selectedTags: (ITag | ILearnisticApp)[]) {
    try {
      const currentCampaign = tempCampaign as Campaign;

      const { _id: campaignId } = currentCampaign;

      let newStopTagConditions = selectedTags.map(
        (st: ITag | ILearnisticApp) => {
          if ((st as any).tags) {
            return st as ILearnisticApp;
          } else {
            return (st as ITag)._id;
          }
        }
      );

      let updatedCampaign = {
        ...currentCampaign,
        stopTagConditions: newStopTagConditions,
      };

      const response = await CampaignApi.updateCampaign(
        campaignId,
        updatedCampaign
      );

      if (response) {
        setTempCampaign(updatedCampaign);
      } else {
        toast("For some reason your campaign doesn't exist anymore", {
          type: "warning",
        });
      }
    } catch (err) {
      toast("There was an error, try again later", { type: "error" });
    }
  }

  const { Component, saveForm } = children({
    updateCampaign,
    tempCampaign,
    updateNodeDescription,
    updateInitialTags,
    updateStopTags,
  });

  return (
    <Container>
      {Component}

      {!initial && (
        <BottomBar>
          <StyledButton
            inverted
            disable={activeCampaign?.tagConditions.length === 0}
            onClick={() => {
              setForm(null);
            }}
          >
            Hide
          </StyledButton>
        </BottomBar>
      )}
    </Container>
  );
};

export default Form;
